<template>
  <responsive-drawer-header
    :badgeColor="theme.unreadBadgeColor"
    :badgeCount="0"
    :color="theme.appBar.other.color"
    :headerBg="theme.appBar.other.bg"
    :closeDrawerTooltip="$t('closeDrawerHint')"
    :openDrawerTooltip="$t('openDrawerHint')"
  >
    <template v-slot:drawer-module-title>{{ mx_moduleTitle }}</template>
    <template v-slot:drawer-subtitle>{{ duty ? dutyPartTitle : '' }}</template>

    <!-- left container -->
    <template v-slot:left-module-title>{{ mx_moduleTitle }}</template>
    <template v-slot:left-subtitle>
      <div class="d-flex" v-if="isLoaded && displayMode === 'document' && displayedDocument">{{ displayedDocument.title }}</div>

      <div class="d-flex align-center justify-start" v-else-if="isLoaded && displayedDutyPart">
        <span v-if="!xs" class="mr-4">{{ $t('myDuty.dutyPart') }}</span>
        <div class="chip" :class="chipColors" v-if="!xs && displayedDutyPart.startToMinutesAfterMidnight">
          {{ mx_time(displayedDutyPart.startToMinutesAfterMidnight, displayedDutyPart) }} -
          {{ mx_time(displayedDutyPart.endToMinutesAfterMidnight, displayedDutyPart) }}
        </div>
        <div class="chip" :class="chipColors" v-else-if="xs && displayedDutyPart.startToMinutesAfterMidnight">
          {{ $t('Abschnitt') }} {{ mx_time(displayedDutyPart.startToMinutesAfterMidnight, displayedDutyPart) }} -
          {{ mx_time(displayedDutyPart.endToMinutesAfterMidnight, displayedDutyPart) }}
        </div>
      </div>
    </template>

    <template v-slot:right>
      <icon-text-btn
        v-if="!xs && isDocumentMode && displayedDutyPart.line && displayedDutyPart.track"
        class="order-0"
        @onClick="displayTour"
        :icon="`mdi-vector-polyline`"
        :text="$t('fd.displayDuty')"
        :hint="$t('fd.displayDuty.descr')"
        :iconColor="color"
        small
      ></icon-text-btn>

      <icon-text-btn
        v-else-if="!xs && isTourMode"
        class="order-1"
        @onClick="displayDocument"
        :icon="`mdi-file-document`"
        :text="$t('fd.displayDocument')"
        :hint="$t('fd.displayDocument.descr')"
        :iconColor="color"
        small
      >
      </icon-text-btn>

      <icon-text-btn
        v-if="!xs"
        @onClick="$router.push({ name: 'myDuties' })"
        class="order-2"
        icon="mdi-arrow-top-left"
        small
        :icon-color="color"
        :text="$t('fd.backToOverview')"
        :hint="$t('fd.backToOverviewDetails')"
      >
      </icon-text-btn>
      <div :key="4" v-if="isDocumentMode && displayedDocument" class="d-flex align-center order-3">
        <!-- Toolbar items for document displayed on medium sized devices and up-->
        <acknowledge-button
          v-if="isToAcknowledge"
          :document="displayedDocument"
          :documentTitle="displayedDocument.title"
          :moduleType="moduleType"
        ></acknowledge-button>
        <toggle-read-button v-if="!mdAndDown" :item="displayedDocument" :color="color"></toggle-read-button>

        <favorite-icon v-if="xl" :item="displayedDocument" :color="color"></favorite-icon>

        <v-menu class="order-4" bottom left transition="slide-y-transition" offset-y close-on-content-click>
          <template v-slot:activator="{ on, attrs }">
            <v-btn :color="color" fab class="d-flex trx-15" icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <!-- display tour -->
            <v-list-item v-if="xs && isDocumentMode && displayedDutyPart.line && displayedDutyPart.track" @click="displayTour">
              <v-list-item-icon>
                <v-icon>mdi-vector-polyline</v-icon>
              </v-list-item-icon>
              <v-list-item-title> {{ $t('fd.displayDuty') }}</v-list-item-title>
            </v-list-item>

            <!-- display document -->
            <v-list-item v-if="xs && isDocumentMode && displayedDutyPart.line && displayedDutyPart.track" @click="displayDocument">
              <v-list-item-icon>
                <v-icon>mdi-file-document</v-icon>
              </v-list-item-icon>
              <v-list-item-title> {{ $t('fd.displayDocument') }}</v-list-item-title>
            </v-list-item>

            <!-- display document -->
            <v-list-item v-if="xs" @onClick="$router.push({ name: 'myDuties' })">
              <v-list-item-icon>
                <v-icon>mdi-arrow-top-left</v-icon>
              </v-list-item-icon>
              <v-list-item-title> {{ $t('fd.backToOverview') }}</v-list-item-title>
            </v-list-item>

            <toggle-read-button v-if="mdAndDown" listItem :item="displayedDocument" :color="color"></toggle-read-button>
            <download v-if="features.enableDownload && features.enableDocumentDownload !== false" listItem :downloadUrl="documentUrl" :color="color"></download>
            <!-- currently not implemented on cockpit side -->
            <favorite-icon v-if="!xl" listItem :item="displayedDocument"></favorite-icon>
            <share-button listItem :file="displayedDocument" :moduleBackendId="moduleBackendId" :color="color"></share-button>

            <acknowledge-button
              listItem
              v-if="!mdAndUp && isToAcknowledge"
              :documentTitle="displayedDocument.title"
              :document="displayedDocument"
              :moduleType="moduleType"
            ></acknowledge-button>
          </v-list>
        </v-menu>
      </div>
    </template>
  </responsive-drawer-header>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import ResponsiveDrawerHeader from '@/layouts/ResponsiveDrawerHeader.vue';
import Download from '@/components/buttons/Download.vue';
import ShareButton from '@/components/buttons/ShareButton.vue';
import ToggleReadButton from '@/components/buttons/ToggleReadButton.vue';
import FavoriteIcon from '@/components/buttons/FavoriteIcon.vue';
import AcknowledgeButton from '@/components/buttons/AcknowledgeButton.vue';
import acknowledgeService from '@/services/acknowledgeService';
import IconTextBtn from '@/components/buttons/IconTextBtn.vue';
import dutyMixin from '../common/dutyMixin';
import MODULE_ID from '../moduleId';

import index from '../index';

const ROOT = {};

export default {
  name: 'Fahrdienst452Header',
  mixins: [common, dutyMixin, configMixin],
  components: {
    ResponsiveDrawerHeader,
    IconTextBtn,
    FavoriteIcon,
    Download,
    ShareButton,
    ToggleReadButton,
    AcknowledgeButton,
  },

  data() {
    return {
      isVisibleHideDrawerTooltip: false,
    };
  },

  computed: {
    ...mapState(MODULE_ID, ['displayedDutyPart', 'isLoaded']),
    ...mapState('fahrdienst452', ['fahrdienst452FileById']),

    moduleType: () => index.moduleType,
    moduleBackendId: () => index.api,
    displayedDocument() {
      return this.fahrdienst452FileById[this.$route.params.documentId];
    },
    displayMode() {
      return this.$route.params.documentId ? 'document' : 'duty';
    },
    color() {
      return this.theme.appBar.other.color;
    },
    chipColors() {
      return `${this.theme.elements.btnTextClz} ${this.theme.elements.btnColor}`;
    },
    duty() {
      if (this.$route.params.dutyId) {
        return this.$store.state[MODULE_ID].dutiesById[this.$route.params.dutyId];
      }
      return null;
    },
    dutyPartTitle() {
      return `${this.mx_getDutyDate(this.duty)} ${this.$t('fd.duty')} ${this.duty.dutyname}`;
    },

    isToAcknowledge() {
      return acknowledgeService.isToAcknowledge(this.displayedDocument);
    },
    documentUrl() {
      if (this.displayedDocument) {
        return this.constructPdfJsFileUrl('fahrdienst452', this.displayedDocument, 'filename');
      }
      return null;
    },
    isDocumentMode() {
      return this.displayMode === 'document';
    },
    isTourMode() {
      return this.displayMode === 'tour';
    },
  },
  methods: {
    ...mapMutations(MODULE_ID, ['setDisplayMode']),
    markAsRead() {
      let sendboxTitle = this.$t('action.read.msg', { title: this.displayedDocument.title });
      let snackbarText = this.$t('action.document.read');
      this.$store.dispatch(
        `sendbox/markAsRead2`,
        {
          item: this.displayedDocument,
          sendboxTitle,
          snackbarText,
          immediate: true,
        },
        ROOT
      );
    },

    markAsUnread() {
      let sendboxTitle = this.$t('action.unread.msg', { title: this.displayedDocument.title });
      let snackbarText = this.$t('action.document.unread');
      this.$store.dispatch(
        `sendbox/markAsUnread2`,
        {
          item: this.displayedDocument,
          sendboxTitle,
          snackbarText,
        },
        ROOT
      );
    },
    displayTour() {
      this.$router.push(`/myduties/detail/${this.$route.params.dutyId}`);
      // this.setDisplayMode('tour');
    },
    displayDocument() {
      this.setDisplayMode('document');
    },
  },
  watch: {
    displayedDutyPart(newValue, oldValue) {},
    displayedDocument(newValue, oldValue) {},
  },
};
</script>
